import React, { useState, useMemo } from "react";
import { useHistory } from "react-router-dom";
import {
  Compass,
  Users,
  Flame,
  Sparkles,
  ArrowUpDown,
  Image as ImageIcon,
  Video,
  Lock,
  Tag,
  SearchX,
} from "lucide-react";
import styles from "./FeedTimeline.module.css";
import FeedStoriesBar from "./FeedStoriesBar";
import FeedSpotlightCard from "./FeedSpotlightCard";
import {
  DUMMY_TIMELINE_DATA,
  DUMMY_POSTS,
  FeedItem,
  FeedPost,
  Post,
} from "./dummyFeedData";
import { useAppSelector } from "../../../redux/typedHooks";
import { UserInterface } from "../../../types/user";
import FeedPostCard from "./FeedPostCard";

interface FeedTimelineProps {
  searchFilter?: string;
}

const CATEGORIES = [
  "All",
  "VIP Exclusive",
  "Travel",
  "Glamour",
  "Lifestyle",
  "Behind The Scenes",
];

const parseLikesNum = (likes: string | number): number => {
  if (typeof likes === "number") return likes;
  const str = String(likes).toUpperCase().trim();
  if (str.endsWith("K")) return parseFloat(str) * 1000;
  if (str.endsWith("M")) return parseFloat(str) * 1000000;
  return parseFloat(str) || 0;
};

export default function FeedTimeline({ searchFilter = "" }: FeedTimelineProps) {
  const history = useHistory();
  const currentUser = useAppSelector<UserInterface | null>((state) => state.user.currentUser);

  const [activeTab, setActiveTab] = useState<
    "explore" | "subscriptions" | "trending" | "vip"
  >("explore");
  const [sortBy, setSortBy] = useState<"popular" | "recent">("popular");
  const [selectedCategory, setSelectedCategory] = useState("All");

  const myAvatar =
    currentUser?.profileImg ||
    "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&auto=format&fit=crop&q=80";

  // Filter & sort feed items based on active tab, search filter, and category
  const filteredFeedItems = useMemo(() => {
    let list: FeedItem[] = [...DUMMY_TIMELINE_DATA];

    // Filter by tab
    if (activeTab === "subscriptions") {
      list = list.filter((item) => item.creator.isFollowing);
    } else if (activeTab === "vip") {
      list = list.filter(
        (item) =>
          item.itemType === "spotlight" ||
          (item.itemType === "post" && item.category === "VIP Exclusive")
      );
    } else if (activeTab === "trending") {
      list = list.filter(
        (item) =>
          item.itemType === "spotlight" ||
          (item.itemType === "post" && parseLikesNum(item.likesCount) > 1000)
      );
    }

    // Filter by category chip
    if (selectedCategory !== "All") {
      list = list.filter((item) =>
        item.itemType === "post"
          ? item.category?.toLowerCase() === selectedCategory.toLowerCase()
          : true
      );
    }

    // Filter by search query
    if (searchFilter.trim()) {
      const q = searchFilter.toLowerCase().trim();
      list = list.filter((item) => {
        const nameMatch = item.creator.name.toLowerCase().includes(q);
        const usernameMatch = item.creator.username.toLowerCase().includes(q);
        if (item.itemType === "post") {
          const captionMatch = item.caption.toLowerCase().includes(q);
          const tagsList = item.hashtags || item.tags || [];
          const tagMatch = tagsList.some((t: string) =>
            t.toLowerCase().includes(q)
          );
          return nameMatch || usernameMatch || captionMatch || tagMatch;
        }
        return nameMatch || usernameMatch;
      });
    }

    // Sort
    if (sortBy === "popular") {
      list.sort((a, b) => {
        const aLikes = a.itemType === "post" ? parseLikesNum(a.likesCount) : 5000;
        const bLikes = b.itemType === "post" ? parseLikesNum(b.likesCount) : 5000;
        return bLikes - aLikes;
      });
    }

    return list;
  }, [activeTab, selectedCategory, searchFilter, sortBy]);

  return (
    <main className={styles.timeline}>
      {/* 1. Creator Stories Carousel */}
      <FeedStoriesBar />

      {/* 2. Feed Navigation Tabs */}
      <div className={styles.tabsContainer}>
        <div className={styles.tabsList}>
          <button
            className={`${styles.tabBtn} ${activeTab === "explore" ? styles.tabBtnActive : ""
              }`}
            onClick={() => setActiveTab("explore")}
          >
            <Compass size={16} />
            <span>Explore</span>
          </button>

          <button
            className={`${styles.tabBtn} ${activeTab === "subscriptions" ? styles.tabBtnActive : ""
              }`}
            onClick={() => setActiveTab("subscriptions")}
          >
            <Users size={16} />
            <span>My Subscriptions</span>
          </button>

          <button
            className={`${styles.tabBtn} ${activeTab === "trending" ? styles.tabBtnActive : ""
              }`}
            onClick={() => setActiveTab("trending")}
          >
            <Flame size={16} />
            <span>Trending</span>
          </button>

          <button
            className={`${styles.tabBtn} ${activeTab === "vip" ? styles.tabBtnActive : ""
              }`}
            onClick={() => setActiveTab("vip")}
          >
            <Sparkles size={16} />
            <span>VIP Exclusives</span>
          </button>
        </div>

        {/* Sort Selector */}
        <div className={styles.sortSelector}>
          <ArrowUpDown size={14} />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as "popular" | "recent")}
          >
            <option value="popular">Popular First</option>
            <option value="recent">Most Recent</option>
          </select>
        </div>
      </div>

      {/* 3. Category Filter Chips */}
      <div className={styles.categoryFilterChips}>
        {CATEGORIES.map((cat) => (
          <button
            key={cat}
            className={`${styles.filterChip} ${selectedCategory === cat ? styles.filterChipActive : ""
              }`}
            onClick={() => setSelectedCategory(cat)}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* 4. Create Post Card Trigger */}
      <div className={styles.createPostCard}>
        <div className={styles.createPostTop}>
          <img src={myAvatar} alt="My profile" className={styles.userAvatar} />
          <div
            className={styles.createPostInputTrigger}
            onClick={() => history.push("/create-post")}
          >
            Share an exclusive moment or behind-the-scenes...
          </div>
        </div>

        <div className={styles.createPostActions}>
          <div className={styles.createPostActionBtns}>
            <button
              className={styles.postActionBtn}
              onClick={() => history.push("/create-post")}
            >
              <ImageIcon size={16} color="#fe5a97" />
              <span>Photo</span>
            </button>

            <button
              className={styles.postActionBtn}
              onClick={() => history.push("/create-post")}
            >
              <Video size={16} color="#60a5fa" />
              <span>Video</span>
            </button>

            <button
              className={styles.postActionBtn}
              onClick={() => history.push("/create-post")}
            >
              <Lock size={16} color="#ffd700" />
              <span>The Vault</span>
            </button>
          </div>

          <button
            className={styles.publishBtn}
            onClick={() => history.push("/create-post")}
          >
            Post
          </button>
        </div>
      </div>

      {/* 5. Posts Feed List */}
      <div className={styles.postsList}>
        {filteredFeedItems.length > 0 ? (
          filteredFeedItems.map((item) =>
            item.itemType === "spotlight" ? (
              <FeedSpotlightCard key={item.id} item={item} />
            ) : (
              <FeedPostCard key={item.id} post={item} />
            )
          )
        ) : (
          <div className={styles.emptyFeed}>
            <div className={styles.emptyIcon}>
              <SearchX size={32} />
            </div>
            <h3 className={styles.emptyTitle}>No posts found</h3>
            <p className={styles.emptyDesc}>
              There are no posts matching your current filters. Try changing your category or search query.
            </p>
            <button
              className={styles.resetBtn}
              onClick={() => {
                setActiveTab("explore");
                setSelectedCategory("All");
              }}
            >
              Reset Filters
            </button>
          </div>
        )}
      </div>
    </main>
  );
}
